'use client';

import React, { useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/Button';

type ConfirmVariant = 'danger' | 'warning' | 'default';

interface ConfirmDialogProps {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: ConfirmVariant;
    /** Show a loading spinner on the confirm button */
    loading?: boolean;
}

const variantStyles: Record<ConfirmVariant, string> = {
    danger:
        'bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 hover:border-rose-500/30',
    warning:
        'bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-500/30',
    default:
        'bg-white text-black hover:bg-zinc-200 border border-white/10',
};

export function ConfirmDialog({
    open,
    onConfirm,
    onCancel,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    variant = 'default',
    loading = false,
}: ConfirmDialogProps) {
    const confirmRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    // Focus the confirm button when the dialog opens
    useEffect(() => {
        if (open) {
            // Small delay to let the DOM render
            const timer = setTimeout(() => confirmRef.current?.focus(), 50);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Trap focus within the dialog
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onCancel();
                return;
            }
            if (e.key === 'Tab' && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
                );
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        },
        [onCancel],
    );

    // Handle backdrop click
    const handleBackdropClick = useCallback(
        (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) onCancel();
        },
        [onCancel],
    );

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={handleBackdropClick}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby={description ? 'confirm-dialog-desc' : undefined}
        >
            <div
                ref={dialogRef}
                className="w-full max-w-md mx-4 rounded-xl border border-border-default bg-bg-surface shadow-2xl shadow-zinc-950/40"
            >
                <div className="px-6 pt-6 pb-2">
                    <h2
                        id="confirm-dialog-title"
                        className="text-base font-semibold text-zinc-100 text-balance"
                    >
                        {title}
                    </h2>
                    {description && (
                        <p
                            id="confirm-dialog-desc"
                            className="mt-2 text-sm text-zinc-400 leading-relaxed text-pretty"
                        >
                            {description}
                        </p>
                    )}
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4">
                    <Button
                        type="button"
                        onClick={onCancel}
                        className="
                            inline-flex items-center justify-center rounded-lg px-3.5 py-2 text-sm font-medium
                            bg-transparent text-zinc-400 hover:bg-bg-elevated hover:text-zinc-200
                            border border-transparent
                            transition-colors duration-150
                            focus-visible:ring-2 focus-visible:ring-zinc-500/50 focus-visible:outline-none
                        "
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        ref={confirmRef}
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className={`
                            inline-flex items-center justify-center rounded-lg px-3.5 py-2 text-sm font-medium
                            transition-colors duration-150
                            active:scale-[0.98]
                            focus-visible:ring-2 focus-visible:ring-zinc-500/50 focus-visible:outline-none
                            disabled:opacity-50 disabled:pointer-events-none
                            ${variantStyles[variant]}
                        `}
                    >
                        {loading && (
                            <svg className="w-4 h-4 animate-spin mr-2" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                        )}
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
}
